/* =============================================================================
   MHV HOME — info-focused home page driver
   Fetches /api/home/cards and powers:
     • the reduced split hero (slideshow left + info card right) that cycles
       through auction/event status cards;
     • the fixed row of manual cards below (consign invite, announcements).
   Reuses MHV.SERVER_URL + MHV.escapeHtml (loaded globally by theme.liquid).
   ============================================================================= */
(function () {
  var MHV = window.MHV || {};
  var SERVER_URL = MHV.SERVER_URL || 'https://mhv-viewer-app-1df0c0c61ed9.herokuapp.com';
  // Quote-safe: esc() is used in quoted src/href/alt attributes (SEC-61), so prefer
  // MHV.escapeAttr (a superset of escapeHtml that also escapes " and '). Over-escaping
  // in text contexts is harmless — the browser decodes the entities on render.
  var esc = (typeof MHV.escapeAttr === 'function') ? MHV.escapeAttr : function (s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };

  var hero = document.getElementById('mhv-home-hero');
  if (!hero) return;

  var slideshow = document.getElementById('mhv-home-slideshow');
  var fallback = document.getElementById('mhv-home-fallback');
  var infoCard = document.getElementById('mhv-home-card');
  var navEl = document.getElementById('mhv-home-nav');
  var dotsEl = document.getElementById('mhv-home-dots');
  var prevBtn = document.getElementById('mhv-home-prev');
  var nextBtn = document.getElementById('mhv-home-next');
  var cardsEl = document.getElementById('mhv-home-cards');

  var STATUS_LABEL = {
    'current-auction': 'Live Now',
    'bidding-open': 'Bidding Open',
    'coming-up': 'Coming Up',
    'postponed': 'Postponed',
    'recently-ended': 'Recently Ended',
    'other-event': 'Event'
  };

  var PHOTO_INTERVAL = 4500;   // ms between photo swaps
  var SLIDES_PER_CARD = 2;     // photos shown before advancing to the next card

  // ── engine state ──
  var cards = [];
  var cardSlides = [];   // cardSlides[i] = array of <div class="mhv-home-slide"> els
  var photoPos = [];     // next photo index per card
  var cardIdx = 0;
  var photosShownForCard = 0;
  var activeSlide = null;
  var multi = false;
  var timer = null;

  function ready() { hero.classList.add('is-ready'); }

  function titleCase(s) {
    return String(s || '').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function fmtDate(card) {
    if (!card.eventDate) return null;
    var d = new Date(card.eventDate + 'T12:00:00');
    if (isNaN(d.getTime())) return null;
    // Short forms: this is a badge beside two others, not a line of prose.
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  // Event start time is provided in Eastern (HH:MM). Render it in the viewer's
  // own timezone as 12-hour am/pm with the local tz abbreviation (e.g. 6:00 PM EDT).
  function fmtTime(card) {
    if (!card.startTime || !card.eventDate) return null;
    var d;
    // Use the canonical Eastern→UTC converter (exact DST boundaries) so the home
    // hero matches the schedule page + hub banner. The old month-based offset was
    // 1 hour off on DST-edge dates (early March / early Nov) — LOG-21.
    if (typeof MHV !== 'undefined' && MHV.easternToUTC) {
      var dp = card.eventDate.slice(0, 10).split('-');
      var tp = card.startTime.split(':');
      d = MHV.easternToUTC(parseInt(dp[0], 10), parseInt(dp[1], 10), parseInt(dp[2], 10), parseInt(tp[0], 10), parseInt(tp[1] || 0, 10));
    } else {
      var month = parseInt(card.eventDate.slice(5, 7), 10);
      var offset = (month >= 3 && month <= 11) ? '-04:00' : '-05:00'; // EDT vs EST (fallback)
      d = new Date(card.eventDate + 'T' + card.startTime + ':00' + offset);
    }
    if (!d || isNaN(d.getTime())) return null;
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
  }

  /* A timed card: the server's own phase names say so; before sale day the type pill
     (lib/auction-types publicLabel) is the only sign the payload carries. */
  function isTimedCard(card) {
    return !!(card.timed || /^timed-/.test(card.phase || '') || card.typeLabel === 'Online Timed Auction');
  }

  // Eastern wall clock + its zone for that date ("6:00 PM EST") — a timed sale's clock.
  function fmtEasternZ(card) {
    if (!card.startTime || !card.eventDate || typeof MHV === 'undefined' || !MHV.easternToUTC) return fmtTime(card);
    var dp = card.eventDate.slice(0, 10).split('-');
    var tp = card.startTime.split(':');
    var d = MHV.easternToUTC(parseInt(dp[0], 10), parseInt(dp[1], 10), parseInt(dp[2], 10), parseInt(tp[0], 10), parseInt(tp[1] || 0, 10));
    if (!d || isNaN(d.getTime())) return null;
    return d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
  }

  // An in-person start time is the time on the DOOR — Eastern, as scheduled, with no zone
  // label (owner, 2026-10-12: "only show the edt / est badge if the event is online").
  function fmtWallTime(card) {
    var m = /^(\d{1,2}):(\d{2})/.exec(card.startTime || '');
    if (!m) return null;
    var h = parseInt(m[1], 10);
    return (h % 12 || 12) + ':' + m[2] + ' ' + (h < 12 ? 'AM' : 'PM');
  }

  // Date / time / where, as badges (owner, 2026-10-12):
  //   ONLINE   — the time in the viewer's own zone WITH its EDT/EST label (they join from
  //              anywhere), and NO location badge — there is nowhere to come to.
  //   IN PERSON — the Eastern time with no zone label, and the town.
  function metaBits(card) {
    var bits = [];
    var online = card.format === 'online-only';
    var date = fmtDate(card);
    if (date) bits.push({ text: date });
    // A postponed sale has no date — the chip says so (the status line says why).
    else if (card.status === 'postponed') bits.push({ text: 'Date TBA' });
    // ⏱️ An ONLINE TIMED CLOSE sale (2026-10-25): its time is when lots START CLOSING,
    // in Eastern WITH the zone (the server's description prints the same), and the chip
    // says so — a bare "6:00 PM EST" reads as the start of a live auction.
    var timed = isTimedCard(card);
    var time = card.status === 'postponed' ? null
      : timed ? fmtEasternZ(card) : (online ? fmtTime(card) : fmtWallTime(card));
    if (time) bits.push({ text: timed ? 'Lots close from ' + time : time });
    if (!online && card.location) bits.push({ text: titleCase(card.location) + ', NH' });
    return bits;
  }

  // Every row is reached by CLASS, never by id, so the same renderer fills the real card AND the
  // hidden twin the height is measured from — see lockCardHeight. Two renderers is how the twin
  // starts measuring something the card no longer draws.
  function els(root) {
    return {
      eyebrow: root.querySelector('.mhv-home-eyebrow'),
      heading: root.querySelector('.mhv-home-hero__heading'),
      meta: root.querySelector('.mhv-home-hero__meta'),
      sub: root.querySelector('.mhv-home-hero__sub'),
      status: root.querySelector('.mhv-home-status'),
      desc: root.querySelector('.mhv-home-hero__desc'),
      more: root.querySelector('.mhv-home-hero__readmore'),
      cta: root.querySelector('.mhv-home-hero__cta')
    };
  }
  var mainEls = els(infoCard);

  // A row a card has nothing for is REMOVED, not blanked — an empty slot holding its space reads as
  // bad spacing, which is what reserving one per row looked like. The column is free to be whatever
  // shape the card is; the BOX it sits in is what is held still (lockCardHeight).
  function show(el, on) { if (el) el.style.display = on ? '' : 'none'; }

  function renderInfo(card, e) {
    e = e || mainEls;
    // ⚠️ THE PILL IS THE EVENT'S TYPE now ("In-Person Antique Auction"), not its status —
    // the status has its own line under the chips, worded for where the sale actually is.
    var pill = card.typeLabel || STATUS_LABEL[card.status] || '';
    show(e.eyebrow, !!pill);
    e.eyebrow.textContent = pill;
    if (e.status) {
      show(e.status, !!card.statusText);
      if (card.phase) e.status.setAttribute('data-phase', card.phase); else e.status.removeAttribute('data-phase');
      e.status.innerHTML = card.statusText
        ? '<span class="mhv-home-status__dot"></span>' + esc(card.statusText) : '';
    }
    e.heading.textContent = card.heading || '';
    var bits = metaBits(card);
    show(e.meta, bits.length > 0);
    e.meta.innerHTML = bits.map(function (m) {
      return '<span class="mhv-home-chip' + (m.kind ? ' mhv-home-chip--' + esc(m.kind) : '') + '">'
        + esc(m.text) + '</span>';
    }).join('');
    // Subheading = the card's promo line; description = the event's own blurb.
    show(e.sub, !!card.subheading);
    e.sub.textContent = card.subheading || '';
    // ⚠️ "Read more" is a row of its own, NOT the tail of the blurb: the blurb is line-clamped, and
    // inside the clamp the link is the first thing to be cut — losing it on exactly the card whose
    // description was long enough to need it.
    var text = card.description ? String(card.description) : '';
    // ⚠️ Keep this inside the blurb's three clamped lines (mhv-home-2.css): past them the clamp eats
    // words silently, and a sentence that stops mid-word is worse than a shorter one.
    var LIMIT = 150;
    var truncated = false;
    if (text.length > LIMIT) {
      var cut = text.slice(0, LIMIT);
      var lastSpace = cut.lastIndexOf(' ');
      if (lastSpace > 100) cut = cut.slice(0, lastSpace);
      text = cut + '…';
      truncated = true;
    }
    show(e.desc, !!text);
    e.desc.textContent = text;
    var ctaLink = card.ctaLink || '/pages/schedule';
    e.cta.textContent = card.ctaText || 'Learn More';
    e.cta.href = ctaLink;
    // ⚠️ The event-details link earns its place two different ways, and only one of them is about
    // the blurb. Where the button already goes to the schedule (the default), the link duplicates it
    // and only appears when the blurb was actually clipped. Where the button goes somewhere else —
    // the catalogue, a ticket page — the schedule entry is otherwise unreachable from this card, so
    // it shows whether or not anything was cut.
    if (e.more) {
      var ctaToSchedule = ctaLink.indexOf('/pages/schedule') === 0;
      show(e.more, !!card.eventId && (truncated || !ctaToSchedule));
      // ⚠️ ALWAYS "Event details", whichever reason it is showing for. It goes to the same place
      // either way, and a link that renames itself according to whether a sentence happened to run
      // long is one the reader has to work out twice.
      if (card.eventId) e.more.href = '/pages/schedule#event-' + card.eventId;
      else e.more.removeAttribute('href');
    }
  }

  // ⚠️ ONE MEASURED HEIGHT, NOT SIX RESERVED ROWS. The hero swaps cards in place, so the column
  // changing shape between them reads as a stutter — but padding every row out to a fixed height
  // leaves a gap wherever a card has nothing to put, and a card using three of the six rows then
  // looks badly spaced. So each card is rendered into a hidden twin of the card, the tallest is
  // measured, and that single number becomes the real card's min-height. Rows come and go normally;
  // only the box is pinned, and the content centres inside it.
  // ⚠️ Measured from a TWIN rather than the card itself because the card is a stretched grid item —
  // its own offsetHeight is the row's height, not its content's, so it cannot measure itself.
  var measurer = null;
  function lockCardHeight() {
    if (!cards.length || !infoCard) return;
    var w = infoCard.getBoundingClientRect().width;
    if (!w) return;
    if (!measurer) {
      measurer = infoCard.cloneNode(true);
      measurer.removeAttribute('id');
      Array.prototype.forEach.call(measurer.querySelectorAll('[id]'), function (el) { el.removeAttribute('id'); });
      measurer.setAttribute('aria-hidden', 'true');
      measurer.classList.remove('is-fading');
      measurer.classList.add('mhv-home-measure');
      measurer.style.minHeight = '0px';
      infoCard.parentNode.appendChild(measurer);
      measurer._els = els(measurer);
    }
    measurer.style.width = w + 'px';
    var tallest = 0;
    cards.forEach(function (card) {
      renderInfo(card, measurer._els);
      var h = measurer.offsetHeight;
      if (h > tallest) tallest = h;
    });
    infoCard.style.minHeight = tallest ? tallest + 'px' : '';
  }

  function renderDots(activeIdx) {
    if (navEl) navEl.style.display = multi ? '' : 'none';
    if (!dotsEl) return;
    dotsEl.innerHTML = '';
    if (!multi) return;
    cards.forEach(function (card, i) {
      var btn = document.createElement('button');
      btn.className = 'mhv-home-dot' + (i === activeIdx ? ' active' : '');
      btn.type = 'button';
      btn.setAttribute('aria-label', card.heading || card.typeLabel || 'Event');
      btn.title = card.heading || card.typeLabel || 'Event';
      btn.addEventListener('click', function () { goToCard(i, true); });
      dotsEl.appendChild(btn);
    });
  }

  function goRelative(delta) {
    if (!multi) return;
    var next = ((cardIdx + delta) % cards.length + cards.length) % cards.length;
    goToCard(next, true);
  }

  // ⚠️ A GALLERY PHOTO IS WHATEVER CAME OFF A PHONE — 4000px wide and several MB. Decoding one of
  // those on the main thread drops frames, and the ambient drift is running while it happens, so
  // the cost is visible as a stutter. Ask Shopify's CDN for a hero-sized rendition instead.
  // ⚠️ Only cdn.shopify.com: our own R2 host (lib/media.js) has sized copies under a NAME pattern,
  // not a query parameter, and a ?width= it does not understand is at best ignored.
  function sizedPhoto(url) {
    if (!url || url.indexOf('cdn.shopify.com') === -1) return url;
    if (/[?&]width=/.test(url)) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=1600';
  }

  function makeSlide(url, alt) {
    var div = document.createElement('div');
    div.className = 'mhv-home-slide';
    var img = document.createElement('img');
    img.alt = alt || '';
    img.decoding = 'async';
    // Resolved once the pixels are actually ready to paint — see swapTo.
    div._ready = new Promise(function (done) {
      img.addEventListener('load', function () {
        if (img.decode) img.decode().then(done, done); else done();
      });
      img.addEventListener('error', done);   // a dead photo must not stall the rotation
    });
    img.src = sizedPhoto(url);   // preloads
    div.appendChild(img);
    return div;
  }

  function after(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  // ⚠️ A SLIDE IS NOT SHOWN UNTIL ITS PIXELS EXIST. Appending an undecoded image and starting the
  // crossfade shows a black frame that snaps into the photo partway through — which looks exactly
  // like the crop jumping, and is worst between events, where a photo of a different shape is
  // reaching object-fit for the first time. The token makes a slow photo LOSE to a later swap
  // rather than landing on top of it.
  var swapToken = 0;
  function swapTo(slide) {
    if (!slide || slide === activeSlide) return;
    var token = ++swapToken;
    Promise.race([slide._ready || Promise.resolve(), after(1500)]).then(function () {
      if (token !== swapToken || slide === activeSlide) return;
      slideshow.appendChild(slide);
      void slide.offsetHeight;  // force reflow so the opacity transition runs
      slide.classList.add('active');
      var prev = activeSlide;
      if (prev) prev.classList.remove('active');
      activeSlide = slide;
      if (prev) setTimeout(function () {
        if (prev.parentNode === slideshow) slideshow.removeChild(prev);
      }, 1200);
    });
  }

  function nextPhotoForCard(idx) {
    var slides = cardSlides[idx];
    if (!slides || slides.length === 0) return null;
    var pos = photoPos[idx] % slides.length;
    photoPos[idx] = (pos + 1) % slides.length;
    return slides[pos];
  }

  function setCard(idx, faded) {
    cardIdx = idx;
    photosShownForCard = 0;
    if (faded) {
      infoCard.classList.add('is-fading');
      setTimeout(function () {
        renderInfo(cards[idx]);
        renderDots(idx);
        infoCard.classList.remove('is-fading');
      }, 400);
    } else {
      renderInfo(cards[idx]);
      renderDots(idx);
    }
    swapTo(nextPhotoForCard(idx));
  }

  function goToCard(idx, userInitiated) {
    if (idx === cardIdx) return;
    setCard(idx, true);
    if (userInitiated) schedule(); // restart the rotation timer
  }

  function tick() {
    photosShownForCard++;
    var slides = cardSlides[cardIdx] || [];
    // Dwell on every card for SLIDES_PER_CARD intervals — a single-photo card
    // simply holds its one photo for that whole time (same as a 2-photo card).
    var switchCard = multi && photosShownForCard >= SLIDES_PER_CARD;
    if (switchCard) {
      setCard((cardIdx + 1) % cards.length, true);
    } else if (slides.length > 1) {
      swapTo(nextPhotoForCard(cardIdx));
    }
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(function () { tick(); schedule(); }, PHOTO_INTERVAL);
  }

  function run(cardList) {
    cards = cardList;
    multi = cards.length > 1;
    cardSlides = cards.map(function (card) {
      // Capped for the same reason the schedule card is: an event's photo set can now
      // be every lot in the sale, and the hero would build a slide element — and pull a
      // photo — for each one. It shows two per rotation; twelve is already more than it
      // will ever get through.
      return (card.photos || []).slice(0, 12).map(function (url) { return makeSlide(url, card.heading); });
    });
    photoPos = cards.map(function () { return 0; });
    // Show first card + its first photo immediately.
    setCard(0, false);
    if (cards.some(function (c, i) { return (cardSlides[i] || []).length > 0; })) schedule();
    // ⚠️ Measure again once the webfonts land — the first pass runs in the fallback face and comes
    // out short, which is a card that grows a few pixels the moment the real type arrives.
    lockCardHeight();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(lockCardHeight).catch(function () {});
    // ⚠️ The hero fades itself in; do that once the FIRST photo can be painted, or the fade-in is
    // a fade onto a black rectangle. Capped, so a dead CDN still reveals the text.
    var first = (cardSlides[0] || [])[0];
    Promise.race([first && first._ready ? first._ready : Promise.resolve(), after(1800)]).then(ready);
  }

  function startFallback() {
    var urls = fallback
      ? Array.prototype.slice.call(fallback.querySelectorAll('img'))
          .map(function (i) { return i.getAttribute('src'); }).filter(Boolean)
      : [];
    run([{
      status: null,
      heading: 'Granite Union Auction Company',
      subheading: 'Auctions, antiques, and more — see our schedule for upcoming events.',
      ctaText: 'See the Schedule',
      ctaLink: '/pages/schedule',
      photos: urls
    }]);
  }

  function renderBelow(below) {
    if (!cardsEl) return;
    if (!below || !below.length) { cardsEl.innerHTML = ''; return; }
    cardsEl.innerHTML = below.map(function (c) {
      var img = c.imageUrl || (c.photos && c.photos[0]) || '';
      var typeClass = c.cardType === 'consign' ? ' mhv-home-card--consign'
        : c.cardType === 'announcement' ? ' mhv-home-card--announcement' : '';
      var h = '<article class="mhv-home-card' + typeClass + '"'
        + (c.accent ? ' data-accent="' + esc(c.accent) + '"' : '') + '>';
      if (img) {
        h += '<div class="mhv-home-card__media"><img src="' + esc(img) + '" alt="'
          + esc(c.title) + '" loading="lazy"></div>';
      }
      h += '<div class="mhv-home-card__body">';
      if (c.eyebrow) h += '<div class="mhv-home-card__eyebrow">' + esc(c.eyebrow) + '</div>';
      h += '<h3 class="mhv-home-card__title">' + esc(c.title) + '</h3>';
      if (c.body) h += '<p class="mhv-home-card__text">' + esc(c.body) + '</p>';
      if (c.ctaText && c.ctaLink) {
        h += '<div class="mhv-home-card__cta"><a href="' + esc(c.ctaLink) + '">'
          + esc(c.ctaText) + '</a></div>';
      }
      h += '</div></article>';
      return h;
    }).join('');
  }

  if (prevBtn) prevBtn.addEventListener('click', function () { goRelative(-1); });
  if (nextBtn) nextBtn.addEventListener('click', function () { goRelative(1); });

  var relockTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(relockTimer);
    relockTimer = setTimeout(lockCardHeight, 180);
  });

  fetch(SERVER_URL + '/api/home/cards')
    .then(function (r) { return r.json(); })
    .then(function (data) {
      renderBelow(data.below || []);
      var heroCards = (data.hero || []).filter(function (c) { return (c.photos || []).length > 0; });
      if (heroCards.length === 0) startFallback(); else run(heroCards);
    })
    .catch(function (err) {
      console.warn('Home cards fetch failed:', err.message);
      startFallback();
    });
})();
